import { useEffect, useEffectEvent, useRef } from "react";
import type { RefObject } from "react";

/**
 * Keeps a native `<dialog>` in step with a React `open` flag.
 *
 * Six dialogs — the gallery's contact and sell forms, the work's agent
 * assignment, the provenance history entry, the estate's studio registration
 * and the viewing room's work picker — each carried the same effect. The
 * dialog is opened with `showModal()` rather than the `open` attribute so the
 * browser owns the backdrop, focus trap and Escape; the price is that React
 * cannot express "modal" declaratively and something has to drive it.
 *
 * Both calls are guarded: `showModal()` on an already-modal dialog throws
 * InvalidStateError, which would take the screen down on a re-render, and
 * `close()` on a closed one is a no-op only by luck of the spec. The view still
 * owns `onClose` on the element, because Escape and the backdrop close the
 * dialog natively and that handler is what tells React it happened.
 *
 * `onOpened` runs after every render that finds the flag true, not only the
 * one that called `showModal()` — the picker uses it to move focus into its
 * search field, and needs that to survive the dialog already being open.
 */
export function useModalDialog(
  open: boolean,
  onOpened?: () => void,
): RefObject<HTMLDialogElement | null> {
  const dialog = useRef<HTMLDialogElement>(null);
  const opened = useEffectEvent(() => onOpened?.());

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;

    if (open) {
      if (!element.open) element.showModal();
      opened();
      return;
    }

    if (element.open) element.close();
  }, [open]);

  return dialog;
}
